import { useState, useEffect } from 'react';
import axios from 'axios';
import DateFilter, { filterByDateRange } from '../components/DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorPerformance() {
  const [vendorStats, setVendorStats] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorPOs, setVendorPOs] = useState([]);
  const [vendorChallans, setVendorChallans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pos');
  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });
  const [sortField, setSortField] = useState('quality_score');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchVendorPerformance();
  }, [dateFilters]);

  const fetchVendorPerformance = async () => {
    try {
      const params = {};
      if (dateFilters.startDate) params.start_date = dateFilters.startDate;
      if (dateFilters.endDate) params.end_date = dateFilters.endDate;
      
      const response = await axios.get(`${API}/vendor-performance`, { params });
      setVendorStats(response.data);
    } catch (error) {
      console.error('Error fetching vendor performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewVendorDetails = async (vendor) => {
    try {
      const [posRes, challansRes] = await Promise.all([
        axios.get(`${API}/jobwork/pos`, { params: { vendor_id: vendor.id } }),
        axios.get(`${API}/jobwork/challans`, { params: { vendor_id: vendor.id } })
      ]);
      setVendorPOs(posRes.data);
      setVendorChallans(challansRes.data);
      setSelectedVendor(vendor);
      setActiveTab('pos');
    } catch (error) {
      console.error('Error fetching vendor details:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    if (score >= 50) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getRateColor = (rate, inverse = false) => {
    if (inverse) {
      if (rate <= 2) return 'text-green-600';
      if (rate <= 5) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (rate >= 95) return 'text-green-600';
    if (rate >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Sort vendors
  const sortedVendors = [...vendorStats].sort((a, b) => {
    let aVal = a[sortField] || 0;
    let bVal = b[sortField] || 0;
    if (sortOrder === 'asc') {
      return aVal - bVal;
    }
    return bVal - aVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Summary stats
  const totalVendors = vendorStats.length;
  const totalInspected = vendorStats.reduce((sum, v) => sum + v.total_inspected, 0);
  const totalApproved = vendorStats.reduce((sum, v) => sum + v.total_approved, 0);
  const totalRejected = vendorStats.reduce((sum, v) => sum + v.total_rejected, 0);
  const overallAcceptance = totalInspected > 0 ? Math.round((totalApproved / totalInspected) * 100) : 0;
  const avgQualityScore = vendorStats.length > 0
    ? Math.round(vendorStats.reduce((sum, v) => sum + v.quality_score, 0) / vendorStats.length)
    : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Vendor Performance Dashboard</h1>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilters} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Total Vendors</p>
          <p className="text-2xl font-bold text-gray-900">{totalVendors}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Total Inspected</p>
          <p className="text-2xl font-bold text-blue-600">{totalInspected.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Total Approved</p>
          <p className="text-2xl font-bold text-green-600">{totalApproved.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Total Rejected</p>
          <p className="text-2xl font-bold text-red-600">{totalRejected.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Acceptance Rate</p>
          <p className={`text-2xl font-bold ${getRateColor(overallAcceptance)}`}>
            {overallAcceptance}%
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-600">Avg Quality Score</p>
          <p className={`text-2xl font-bold ${getRateColor(avgQualityScore)}`}>
            {avgQualityScore}
          </p>
        </div>
      </div>

      {/* Vendor Performance Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Vendor Quality Rankings</h2>
          <p className="text-sm text-gray-500 mt-1">Click column headers to sort. Ranked by overall quality score (60% acceptance + 20% on-time + 20% low rework)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('quality_score')}
                >
                  Score {getSortIcon('quality_score')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_inspected')}
                >
                  Inspected {getSortIcon('total_inspected')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_approved')}
                >
                  Accepted {getSortIcon('total_approved')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_rejected')}
                >
                  Rejected {getSortIcon('total_rejected')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_rework')}
                >
                  Rework {getSortIcon('total_rework')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('acceptance_rate')}
                >
                  Accept % {getSortIcon('acceptance_rate')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('on_time_rate')}
                >
                  On-Time {getSortIcon('on_time_rate')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_pos')}
                >
                  POs {getSortIcon('total_pos')}
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_challans')}
                >
                  Challans {getSortIcon('total_challans')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedVendors.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                    No vendor performance data available.
                  </td>
                </tr>
              ) : (
                sortedVendors.map((vendor, index) => (
                  <tr key={vendor.id} className={`hover:bg-gray-50 ${vendor.rejection_rate > 10 ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-4 text-sm font-bold text-gray-900">
                      {index === 0 && vendor.quality_score >= 90 ? '🥇' : 
                       index === 1 && vendor.quality_score >= 80 ? '🥈' : 
                       index === 2 && vendor.quality_score >= 70 ? '🥉' : `#${index + 1}`}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                      <div className="text-xs text-gray-500">{vendor.contact_person}</div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full font-bold ${getScoreColor(vendor.quality_score)}`}>
                        {vendor.quality_score}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-center font-medium">
                      {vendor.total_inspected.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-center font-medium text-green-600">
                      {vendor.total_approved.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-center">
                      {vendor.total_rejected > 0 ? (
                        <span className="font-medium text-red-600">{vendor.total_rejected.toLocaleString()}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-center">
                      {vendor.total_rework > 0 ? (
                        <span className="font-medium text-orange-600">{vendor.total_rework.toLocaleString()}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-center">
                      <span className={`font-semibold ${getRateColor(vendor.acceptance_rate)}`}>
                        {vendor.acceptance_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-center">
                      <span className={`font-semibold ${getRateColor(vendor.on_time_rate)}`}>
                        {vendor.on_time_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-center text-gray-700">
                      {vendor.total_pos}
                    </td>
                    <td className="px-4 py-4 text-sm text-center text-gray-700">
                      {vendor.total_challans}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <button
                        onClick={() => viewVendorDetails(vendor)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quality Legend */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-900 mb-2">Quality Score Guide</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-green-100 text-green-600 font-medium">90-100</span>
            <span>Excellent</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-600 font-medium">70-89</span>
            <span>Good</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-orange-100 text-orange-600 font-medium">50-69</span>
            <span>Needs Improvement</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-red-100 text-red-600 font-medium">0-49</span>
            <span>Poor</span>
          </div>
        </div>
      </div>

      {/* Vendor Details Modal */}
      {selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedVendor.name}</h2>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className={`px-3 py-1 rounded-full font-bold ${getScoreColor(selectedVendor.quality_score)}`}>
                      Score: {selectedVendor.quality_score}
                    </span>
                    <span className="text-gray-600">
                      Acceptance: <span className={`font-semibold ${getRateColor(selectedVendor.acceptance_rate)}`}>
                        {selectedVendor.acceptance_rate}%
                      </span>
                    </span>
                    <span className="text-gray-600">
                      On-Time: <span className={`font-semibold ${getRateColor(selectedVendor.on_time_rate)}`}>
                        {selectedVendor.on_time_rate}%
                      </span>
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedVendor(null)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Quality Stats */}
              <div className="grid grid-cols-5 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Inspected</p>
                  <p className="text-lg font-bold">{selectedVendor.total_inspected}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Approved</p>
                  <p className="text-lg font-bold text-green-600">{selectedVendor.total_approved}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Rejected</p>
                  <p className="text-lg font-bold text-red-600">{selectedVendor.total_rejected}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Rework</p>
                  <p className="text-lg font-bold text-orange-600">{selectedVendor.total_rework}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Reject Rate</p>
                  <p className={`text-lg font-bold ${getRateColor(100 - selectedVendor.rejection_rate)}`}>
                    {selectedVendor.rejection_rate}%
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b mt-4">
                <button
                  onClick={() => setActiveTab('pos')}
                  className={`px-4 py-2 font-medium ${activeTab === 'pos' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
                >
                  Purchase Orders ({vendorPOs.length})
                </button>
                <button
                  onClick={() => setActiveTab('challans')}
                  className={`px-4 py-2 font-medium ${activeTab === 'challans' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
                >
                  Challans ({vendorChallans.length})
                </button>
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'pos' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expected</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {vendorPOs.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No POs found</td></tr>
                    ) : (
                      vendorPOs.map(po => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-blue-600">{po.po_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatDate(po.po_date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatDate(po.expected_completion_date)}</td>
                          <td className="px-4 py-3 text-sm text-center">{po.items?.length || 0}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              po.status === 'Completed' ? 'bg-green-100 text-green-800' :
                              po.status === 'Rejected' || po.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {po.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold">
                            Rs.{(po.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'challans' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Challan No</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {vendorChallans.length === 0 ? (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No challans found</td></tr>
                    ) : (
                      vendorChallans.map(ch => {
                        const totalValue = ch.items?.reduce((sum, item) => 
                          sum + (item.rate_per_piece ? item.quantity * item.rate_per_piece : 0), 0) || 0;
                        return (
                          <tr key={ch.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-blue-600">{ch.challan_number}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{formatDate(ch.challan_date)}</td>
                            <td className="px-4 py-3 text-sm text-center">{ch.items?.length || 0}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                ch.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                ch.status === 'Received - Pending QC' ? 'bg-purple-100 text-purple-800' :
                                ch.status === 'Partially Received' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {ch.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">
                              {totalValue > 0 ? `Rs.${totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
